import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Home, Briefcase, Mail, SearchX } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { usePageSEO } from "@/hooks/usePageSEO";

const NotFound = () => {
  const location = useLocation();
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);

  usePageSEO({
    title: lang === "ar" ? "الصفحة غير موجودة | وافاي" : "Page Not Found | Wavai",
    description: "The page you're looking for doesn't exist or may have been moved.",
    noindex: true,
  });

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 lg:pt-24">
        <section className="py-12 lg:py-32">
          <div className="container px-6 max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-8">
              <SearchX className="h-10 w-10 text-primary" />
            </div>

            <h1 className="text-6xl sm:text-7xl font-display font-bold text-primary mb-4">404</h1>
            <h2 className="text-2xl sm:text-3xl font-display font-semibold mb-4">
              {t("Page Not Found")}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-md mx-auto">
              {t("The page you're looking for doesn't exist or may have been moved. Let's get you back on track.")}
            </p>

            <Button variant="hero" size="lg" asChild className="mb-12">
              <Link to={localePath("/")}>
                <Home className="h-4 w-4 mr-2" />
                {t("Back to Homepage")}
              </Link>
            </Button>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <span className="text-muted-foreground">{t("Or try these:")}</span>
              <Link to={localePath("/services")} className="inline-flex items-center gap-1.5 text-primary hover:text-primary/80 font-medium transition-colors">
                <Briefcase className="h-3.5 w-3.5" />
                {t("Services")}
              </Link>
              <Link to={localePath("/contact")} className="inline-flex items-center gap-1.5 text-primary hover:text-primary/80 font-medium transition-colors">
                <Mail className="h-3.5 w-3.5" />
                {t("Contact")}
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NotFound;
